import type { MigrationStep, MigrationStepGroup, MigrationStepGroupId, MigrationStepTone } from './migration-step-groups';
import { getMigrationStepTone, MIGRATION_STEP_GROUP_ORDER, MIGRATION_STEP_TONE_ORDER } from './migration-step-groups';

export const MIGRATION_STEP_TONE_DOT_COLORS = {
	created: 'var(--ui-success)',
	changed: 'var(--ui-border-accented)',
	renamed: 'var(--ui-warning)',
	deleted: 'var(--ui-error)',
} satisfies Record<MigrationStepTone, string>;

const MIGRATION_STEP_PATH_PARAM_KEYS = ['field', 'collection', 'from', 'relation', 'index', 'namespace'] as const;
const COLLECTION_SECTION_PATH_PARAM_KEYS = ['field', 'collection', 'from', 'relation'] as const;

export interface MigrationStepDisplaySection {
	id: string;
	label: string;
	groupId: MigrationStepGroupId | 'collection';
	steps: MigrationStep[];
}

export function getMigrationStepDisplaySections(
	groups: MigrationStepGroup[],
	activeTones: ReadonlySet<MigrationStepTone>,
): MigrationStepDisplaySection[] {
	const sections = new Map<string, MigrationStepDisplaySection>();

	for (const group of groups) {
		for (const step of group.steps) {
			if (activeTones.size > 0 && !activeTones.has(getMigrationStepTone(step.kind))) {
				continue;
			}

			const sectionKey = getSectionKey(group.id, step);
			const section = sections.get(sectionKey.id) ?? {
				id: sectionKey.id,
				label: sectionKey.label,
				groupId: sectionKey.groupId,
				steps: [],
			};

			section.steps.push(step);

			sections.set(sectionKey.id, section);
		}
	}

	return [...sections.values()]
		.map((section) => ({
			...section,
			steps: sortSteps(section.steps),
		}))
		.toSorted(compareSections);
}

export function countMigrationSteps(steps: MigrationStep[]) {
	const counts = emptyCounts();

	for (const step of steps) {
		counts[getMigrationStepTone(step.kind)] += 1;
	}

	return counts;
}

function getSectionKey(groupId: MigrationStepGroupId, step: MigrationStep) {
	const collectionName = getCollectionName(step);

	if (collectionName && (groupId === 'collections' || groupId === 'fields' || groupId === 'relations')) {
		return {
			id: `collection:${collectionName}`,
			label: collectionName,
			groupId: 'collection' as const,
		};
	}

	return {
		id: groupId,
		label: groupId,
		groupId,
	};
}

function getCollectionName(step: MigrationStep) {
	return getMigrationStepPathParts(step, COLLECTION_SECTION_PATH_PARAM_KEYS)[0];
}

export function getMigrationStepPathParts(
	step: MigrationStep,
	keys: readonly string[] = MIGRATION_STEP_PATH_PARAM_KEYS,
) {
	const path = getFirstStepParam(step, keys);

	return splitMigrationStepPath(path);
}

export function splitMigrationStepPath(path?: string) {
	if (!path) {
		return [];
	}

	return path
		.split('.')
		.map((part) => part.trim())
		.filter((part) => part.length > 0);
}

function emptyCounts(): Record<MigrationStepTone, number> {
	return MIGRATION_STEP_TONE_ORDER.reduce((counts, tone) => {
		counts[tone] = 0;

		return counts;
	}, {} as Record<MigrationStepTone, number>);
}

function sortSteps(steps: MigrationStep[]) {
	return steps.toSorted((first, second) => {
		const toneComparison = MIGRATION_STEP_TONE_ORDER.indexOf(getMigrationStepTone(first.kind)) - MIGRATION_STEP_TONE_ORDER.indexOf(getMigrationStepTone(second.kind));

		if (toneComparison !== 0) {
			return toneComparison;
		}

		return first.id.localeCompare(second.id);
	});
}

function compareSections(first: MigrationStepDisplaySection, second: MigrationStepDisplaySection) {
	const orderComparison = getSectionOrder(first) - getSectionOrder(second);

	if (orderComparison !== 0) {
		return orderComparison;
	}

	return first.label.localeCompare(second.label);
}

function getSectionOrder(section: MigrationStepDisplaySection) {
	if (section.groupId === 'collection') {
		return -1;
	}

	return MIGRATION_STEP_GROUP_ORDER.indexOf(section.groupId);
}

function getFirstStepParam(step: MigrationStep, keys: readonly string[]) {
	const params = step.params as Partial<Record<string, string>>;

	for (const key of keys) {
		const value = params[key];

		if (value) {
			return value;
		}
	}
}
